import React, { useEffect, useState } from 'react';
import styled from 'styled-components/macro';
import FieldCalculatorView from '../view/FieldCalculatorView';
import { Tooltip } from '@material-ui/core';
import generateId from '../../../utils/generateId';

const QuestionBlockWrapper = styled.div`
  display: flex;
  align-items: center;
`;

const QuestionBlock = styled.div`
  border: 1px solid #4c84ff;
  border-radius: 6px;
  color: #4c84ff;
  font-weight: 500;
  margin: 6px;
  padding: 6px;
  ${({ $missing }) =>
    $missing ? 'border: 1px solid #f50057; color: #f50057;' : null};
`;

const OpBlock = styled.div`
  color: #000;
  font-weight: 500;
  margin: 6px;
  padding: 6px;
`;

const NumberBlock = styled.div`
  color: #000;
  font-weight: 500;
  margin: 6px;
  padding: 6px;
`;

const FunctionBlock = styled.div`
  color: #000;
  font-weight: 500;
  margin: 6px;
  padding: 6px;
`;

const CommaBlock = styled.div`
  color: #000;
  font-weight: 500;
  margin: 6px;
  padding: 6px;
`;

const CursorPositionBlock = styled.div`
  color: #000;
  font-weight: 500;
  margin: 6px 0;
  padding: 6px 0;
  width: 2px;
`;

const EditablePosition = () => {
  return (
    <CursorPositionBlock
      contentEditable={false}
      data-blocktype="CursorPositionBlock"
    ></CursorPositionBlock>
  );
};

const operationIdToLabel = id => {
  switch (id) {
    case 'openBrackets':
      return '(';
    case 'closeBrackets':
      return ')';
    case 'add':
      return '+';
    case 'subtract':
      return '-';
    case 'multiply':
      return '×';
    case 'divide':
      return '÷';
    default:
      return '';
  }
};

const functionIdToLabel = id => {
  switch (id) {
    case 'min':
      return 'Min';
    case 'max':
      return 'Max';
    case 'avg':
      return 'Average';
    default:
      return '';
  }
};

const FieldCalculatorEngine = ({
  fields,
  onChangedFormula,
  initialFormulaArray = [],
  validationError = ''
}) => {
  const [currentValue, setCurrentValue] = useState('');
  const [formulaArray, setFormulaArray] = useState(initialFormulaArray);
  const [formulaArrayWithIDs, setFormulaArrayWithIDs] = useState([]);
  const [isCurrentlyInFunction, setIsCurrentlyInFunction] = useState(false);
  const [currentFunctionUUID, setCurrentFunctionUUID] = useState(null);
  const [currentlyOpenBrackets, setCurrentlyOpenBrackets] = useState(0);
  const [doingInitialSetup, setDoingInitialSetup] = useState(true);
  const [lastInserted, setLastInserted] = useState('');
  const [currentCaretPosition, setCurrentCaretPosition] = useState(0);
  const [disabledButtons, setDisabledButtons] = useState([
    'closeBrackets',
    'divide',
    'multiply',
    'add',
    'subtract'
  ]);
  const numberButtons = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'];
  const operatorButtons = ['add', 'subtract', 'multiply', 'divide'];
  const functionButtons = ['min', 'max', 'avg'];

  // useEffect(() => {
  //   updateDisplayAndUUIDsBasedOnGivenFormulaArray(initialFormulaArray);
  // }, [initialFormulaArray]);

  useEffect(() => {
    updateDisplayAndUUIDsBasedOnGivenFormulaArray(
      formulaArray,
      doingInitialSetup
    );
  }, [formulaArray]);

  useEffect(() => {
    let el = document.getElementById('field-calculator-value-field');
    let sel = window.getSelection();
    let foundTheOne = false;
    let targetBlock, range;
    if (!el) {
      return;
    }
    range = document.createRange();
    let countOfEditablePositions = -1;
    targetBlock = el.childNodes[0];
    while (targetBlock && !foundTheOne) {
      if (getBlockType(targetBlock) === 'CursorPositionBlock') {
        countOfEditablePositions++;
      }
      if (countOfEditablePositions === currentCaretPosition) {
        foundTheOne = true;
      } else {
        targetBlock = targetBlock.nextElementSibling;
      }
    }
    if (foundTheOne) {
      // let childNodeIndex = Array.from(el.children).indexOf(targetBlock);
      let childNodeIndex = Array.from(el.childNodes).indexOf(targetBlock);
      if (childNodeIndex === -1) {
        return;
      } else {
        range.setStart(el, getChildNodeIndex(el, targetBlock));
        range.collapse(true);

        sel.removeAllRanges();
        sel.addRange(range);
      }
    }
  }, [currentCaretPosition, currentValue]);

  const getBlockType = domObject => {
    return domObject && domObject.dataset ? domObject.dataset.blocktype : null;
  };

  const getPreviousBlockType = domObject => {
    let previousBlock;
    if (!domObject) {
      return null;
    }
    previousBlock = domObject.previousElementSibling;
    while (previousBlock) {
      if (
        previousBlock.dataset &&
        previousBlock.dataset.blocktype &&
        previousBlock.dataset.blocktype !== 'CursorPositionBlock'
      ) {
        return previousBlock.dataset.blocktype;
      }
      previousBlock = previousBlock.previousElementSibling;
    }
    return null;
  };

  const getIsBlockInFunction = domObject => {
    let previousBlock;
    if (!domObject) {
      return false;
    }
    previousBlock = domObject.previousElementSibling;
    while (previousBlock) {
      if (
        previousBlock.dataset &&
        previousBlock.dataset.blocktype &&
        previousBlock.dataset.blocktype !== 'CursorPositionBlock'
      ) {
        return (
          previousBlock.dataset.blocktype === 'function' ||
          !!previousBlock.dataset.parentUuid
        );
      }
      previousBlock = previousBlock.previousElementSibling;
    }
    return false;
  };

  const getBracketCountBefore = domObject => {
    let previousBlock,
      foundUUID,
      count = 0;
    if (!domObject) {
      return count;
    }
    previousBlock = domObject.previousElementSibling;
    while (previousBlock && !foundUUID) {
      if (previousBlock.dataset && previousBlock.dataset.uuid) {
        foundUUID = previousBlock.dataset.uuid;
      }
      previousBlock = previousBlock.previousElementSibling;
    }
    if (foundUUID) {
      formulaArrayWithIDs.every(one => {
        if (one.operator === 'openBrackets') {
          count++;
        } else if (one.operator === 'closeBrackets') {
          count--;
        }
        if (one.uuid === foundUUID) {
          // stop counting if we've reached the end
          return false;
        }
        return true;
      });
    }
    return count;
  };

  const fieldToQuestionBlock = (field, uuid, parentUUID) => {
    return (
      <>
        <Tooltip
          title={field.text}
          arrow
          contentEditable={false}
          data-uuid={uuid}
          data-parent-uuid={parentUUID || undefined}
          data-blocktype="field"
        >
          <QuestionBlock
            $qLabel={field.qLabel}
            $fieldId={field.value}
            $missing={field.missing}
            contentEditable={false}
            data-uuid={uuid}
            data-parent-uuid={parentUUID || undefined}
            data-blocktype="field"
          >
            {field.qLabel}
          </QuestionBlock>
        </Tooltip>
        <EditablePosition />
      </>
    );
  };

  const functionToFunctionBlock = (item, uuid) => {
    return (
      <>
        <FunctionBlock
          $opId={item.operator}
          contentEditable={false}
          data-uuid={uuid}
          data-blocktype="function"
        >
          {functionIdToLabel(item.operator)} (
        </FunctionBlock>
        <EditablePosition />
        {item.values.map((child, childIndex) => {
          if (child.field) {
            const matchingField = fields.find(
              field => field.value === child.field
            );
            if (!matchingField) {
              return '';
            }
            return (
              <>
                {childIndex > 0 ? <CommaBlock>,</CommaBlock> : ' '}
                {fieldToQuestionBlock(matchingField, child.uuid, uuid)}
              </>
            );
          }
        })}
      </>
    );
  };

  const operationToOperatorBlock = (item, uuid) => {
    return (
      <>
        <OpBlock
          $opId={item.operator}
          data-uuid={uuid}
          data-blocktype={item.operator}
        >
          {operationIdToLabel(item.operator)}
        </OpBlock>
        <EditablePosition />
      </>
    );
  };

  const valueToNumberBlock = (item, uuid) => {
    return (
      <>
        <NumberBlock
          $opId={item.value}
          data-uuid={uuid}
          data-blocktype="number"
        >
          {item.value}
        </NumberBlock>
        <EditablePosition />
      </>
    );
  };

  const updateInFunctionStates = (newFormulaWithIDs, newCaretPosition) => {
    const [newIsCurrentlyInFunction, newCurrentFunctionUUID] =
      getInFunctionStates(newFormulaWithIDs, newCaretPosition);
    setIsCurrentlyInFunction(newIsCurrentlyInFunction);
    setCurrentFunctionUUID(newCurrentFunctionUUID);
    return newIsCurrentlyInFunction;
  };

  // checks if caret is currently in a function, and updates isCurrentlyInFunction and currentFunctionUUID
  const getInFunctionStates = (newFormulaWithIDs, newCaretPosition) => {
    let newIsCurrentlyInFunction,
      newCurrentFunctionUUID,
      checkingCaretIndex,
      functionArgumentCount;
    newIsCurrentlyInFunction = false;
    newCurrentFunctionUUID = null;
    if (newFormulaWithIDs.length && newCaretPosition > 0) {
      checkingCaretIndex = 0;
      newFormulaWithIDs.every(item => {
        if (item.values) {
          functionArgumentCount = item.values.length;
          if (
            checkingCaretIndex + functionArgumentCount + 1 >=
            newCaretPosition
          ) {
            newIsCurrentlyInFunction = true;
            newCurrentFunctionUUID = item.uuid;
            return false;
          }
          checkingCaretIndex += functionArgumentCount + 2;
        } else {
          checkingCaretIndex++;
          newIsCurrentlyInFunction = false;
          newCurrentFunctionUUID = null;
        }
        if (checkingCaretIndex === newCaretPosition) {
          return false;
        }
        return true;
      });
    }
    return [newIsCurrentlyInFunction, newCurrentFunctionUUID];
  };

  const deepCopy = obj => JSON.parse(JSON.stringify(obj));

  const updateDisplayAndUUIDsBasedOnGivenFormulaArray = (
    originalGivenArray,
    closeFunctions
  ) => {
    let newCurrentValue,
      newOpenBracketCount,
      newIsCurrentlyInFunction,
      newDisabledButtons,
      newFormulaArrayWithIDs,
      lastItemIndex,
      newEditablePositionCount,
      foundFunctionUUID,
      givenArray;
    givenArray = deepCopy(originalGivenArray);
    newCurrentValue = '';
    newOpenBracketCount = 0;
    newIsCurrentlyInFunction = false;
    newCurrentValue = <EditablePosition />;
    newEditablePositionCount = 0;
    newFormulaArrayWithIDs = [];
    foundFunctionUUID = false;
    lastItemIndex = givenArray.length - 1;
    givenArray.forEach((item, itemIndex) => {
      let uuid = 'formulaPiece' + generateId();
      foundFunctionUUID = uuid;
      let copyofItem = { ...item, uuid };
      if (copyofItem.values) {
        copyofItem.values.forEach(oneSubItem => {
          let subItemUUID = 'functionPiece' + generateId();
          oneSubItem.parentUUID = uuid;
          oneSubItem.uuid = subItemUUID;
        });
      }
      newFormulaArrayWithIDs.push(copyofItem);
      if (item.field) {
        let matchingField = fields.find(field => field.value === item.field);
        if (!matchingField) {
          openSnackbar('warning', 'snackbars.calculator.missing_field');
          matchingField = {
            text: 'Missing Question',
            qLabel: '??',
            value: item.field,
            missing: true
          };
        }
        newCurrentValue = (
          <>
            {newCurrentValue} {fieldToQuestionBlock(matchingField, uuid)}
          </>
        );
        newEditablePositionCount++;
      } else if (item.operator) {
        if (item.values) {
          // it's a function
          newOpenBracketCount += 1;
          newCurrentValue = (
            <>
              {newCurrentValue} {functionToFunctionBlock(copyofItem, uuid)}
            </>
          );
          newEditablePositionCount += item.values.length + 1;
          if (itemIndex !== lastItemIndex || lastInserted === 'closeBrackets') {
            newOpenBracketCount -= 1;
            newCurrentValue = (
              <>
                {newCurrentValue}{' '}
                <OpBlock $opId="closeBrackets" data-blocktype="closeBrackets">
                  )
                </OpBlock>
                <EditablePosition />
              </>
            );
            newEditablePositionCount++;
          } else if (itemIndex === lastItemIndex) {
            newEditablePositionCount++;
          }
        } else {
          newCurrentValue = (
            <>
              {newCurrentValue} {operationToOperatorBlock(item, uuid)}
            </>
          );
          if (item.operator === 'openBrackets') {
            newOpenBracketCount += 1;
          } else if (item.operator === 'closeBrackets') {
            newOpenBracketCount -= 1;
          }
          newEditablePositionCount++;
        }
      } else if (item.value) {
        newCurrentValue = (
          <>
            {newCurrentValue} {valueToNumberBlock(item, uuid)}
          </>
        );
        newEditablePositionCount++;
      }
    });
    if (givenArray.length) {
      let lastItem = givenArray[givenArray.length - 1];
      let lastItemCategory = 'field';
      if (lastItem.value) {
        lastItemCategory = 'number';
      } else if (lastItem.operator) {
        if (functionButtons.includes(lastItem.operator)) {
          if (closeFunctions || lastInserted === 'closeBrackets') {
            lastItemCategory = 'closeBrackets';
            if (doingInitialSetup) {
              newCurrentValue = (
                <>
                  {newCurrentValue}{' '}
                  <OpBlock $opId="closeBrackets" data-blocktype="closeBrackets">
                    )
                  </OpBlock>
                  <EditablePosition />
                </>
              );
            }
          } else {
            lastItemCategory = lastItem.operator;
            newIsCurrentlyInFunction = true;
          }
        } else {
          lastItemCategory = lastItem.operator;
        }
      }
      if (doingInitialSetup) {
        newDisabledButtons = getDisabledButtons(
          lastItemCategory,
          newOpenBracketCount,
          newIsCurrentlyInFunction
        );
        setDisabledButtons(newDisabledButtons);
      }
      setCurrentlyOpenBrackets(newOpenBracketCount);
    }
    setCurrentValue(newCurrentValue);
    setFormulaArrayWithIDs(newFormulaArrayWithIDs);
    setDoingInitialSetup(false);
    if (doingInitialSetup) {
      setCurrentCaretPosition(newEditablePositionCount);
      updateInFunctionStates(newFormulaArrayWithIDs, newEditablePositionCount);
    } else {
      updateInFunctionStates(newFormulaArrayWithIDs, currentCaretPosition);
    }
  };

  const updateDisabledButtons = (
    whatWasClicked,
    newOpenBracketCount,
    isInFunction
  ) => {
    const newDisabledButtons = getDisabledButtons(
      whatWasClicked,
      newOpenBracketCount,
      isInFunction
    );
    setDisabledButtons(newDisabledButtons);
  };

  const getDisabledButtons = (
    previousItem = '',
    newOpenBracketCount,
    isInFunction
  ) => {
    let newDisabledButtons = [];
    switch (previousItem) {
      case null:
      case 'clear':
        newDisabledButtons = ['closeBrackets', ...operatorButtons];
        break;
      case 'openBrackets':
        newDisabledButtons = ['closeBrackets', ...operatorButtons];
        break;
      case 'closeBrackets':
        newDisabledButtons = [
          'fields',
          'openBrackets',
          ...numberButtons,
          ...functionButtons
        ];
        if (!newOpenBracketCount) {
          newDisabledButtons.push('closeBrackets');
        }
        break;
      case 'field':
        newDisabledButtons = [
          'openBrackets',
          'fields',
          ...functionButtons,
          ...numberButtons
        ];
        if (!newOpenBracketCount) {
          newDisabledButtons.push('closeBrackets');
        }
        if (isInFunction) {
          newDisabledButtons = [
            'openBrackets',
            ...operatorButtons,
            ...numberButtons,
            ...functionButtons
          ];
        }
        break;
      case 'add':
      case 'subtract':
      case 'multiply':
      case 'divide':
        newDisabledButtons = ['closeBrackets', ...operatorButtons];
        break;
      case 'function':
      case 'min':
      case 'max':
      case 'avg':
        newDisabledButtons = [
          'openBrackets',
          ...operatorButtons,
          ...numberButtons,
          ...functionButtons
        ];
        if (!newOpenBracketCount) {
          newDisabledButtons.push('closeBrackets');
        }
        break;
      case 'number':
      case '1':
      case '2':
      case '3':
      case '4':
      case '5':
      case '6':
      case '7':
      case '8':
      case '9':
      case '0':
        newDisabledButtons = ['openBrackets', 'fields', ...functionButtons];
        if (!newOpenBracketCount) {
          newDisabledButtons.push('closeBrackets');
        }
        break;
    }
    return newDisabledButtons;
  };

  const onClickNumberButton = button => {
    let newFormulaArray = [...formulaArray];
    let formulaArrayIndex =
      caretPositionToFormulaArrayIndex(currentCaretPosition);
    const lastItem =
      formulaArrayIndex > 0
        ? newFormulaArray[formulaArrayIndex - 1]
        : null || null;
    if (lastItem && lastItem.value) {
      lastItem.value = lastItem.value + button.id;
    } else {
      newFormulaArray = insertOperatorIntoFormulaArray(newFormulaArray, {
        value: button.id
      });
    }
    onChangedFormula(newFormulaArray);
    updateDisabledButtons(
      button.id,
      currentlyOpenBrackets,
      isCurrentlyInFunction
    );
    if (!lastItem || !lastItem.value) {
      setCurrentCaretPosition(currentCaretPosition + 1);
    }
    setLastInserted('number');
    setFormulaArray(newFormulaArray);
    // updateDisplayAndUUIDsBasedOnGivenFormulaArray(newFormulaArray);
  };

  const onClickFunctionButton = button => {
    let newFormulaArray = [...formulaArray];
    newFormulaArray = insertOperatorIntoFormulaArray(newFormulaArray, {
      operator: button.id,
      values: []
    });
    // const newValue = (
    //   <>
    //     {currentValue}
    //     <FunctionBlock $opId={button.id} contentEditable={false}>
    //       {button.label} (
    //     </FunctionBlock>
    //     <EditablePosition />
    //   </>
    // );
    let newOpenBracketCount = currentlyOpenBrackets;
    onChangedFormula(newFormulaArray);
    updateDisabledButtons(button.id, currentlyOpenBrackets, true);
    setCurrentlyOpenBrackets(newOpenBracketCount + 1);
    setCurrentCaretPosition(currentCaretPosition + 1);
    setFormulaArray(newFormulaArray);
    setLastInserted('function');
    // setCurrentValue(newValue);
  };

  const caretPositionToFormulaArrayIndex = caretPosition => {
    let formulaArrayIndex = 0;
    let checkingCaretIndex = 0;
    formulaArrayWithIDs.every(item => {
      if (checkingCaretIndex >= caretPosition) {
        return false;
      } else {
        if (item.values) {
          checkingCaretIndex += item.values.length + 2;
          formulaArrayIndex++;
        } else {
          checkingCaretIndex++;
          formulaArrayIndex++;
        }
        return true;
      }
    });
    return formulaArrayIndex;
  };

  const insertOperatorIntoFormulaArray = (existingFormulaArray, newItem) => {
    let newFormulaArray;
    if (currentCaretPosition === -1) {
      newFormulaArray = [...existingFormulaArray, newItem];
    } else {
      newFormulaArray = [...existingFormulaArray];
      newFormulaArray.splice(
        caretPositionToFormulaArrayIndex(currentCaretPosition),
        0,
        newItem
      );
    }
    return newFormulaArray;
  };

  const onClickButton = button => {
    let newFormulaArray = [...formulaArray];

    if (!isCurrentlyInFunction || button.id !== 'closeBrackets') {
      newFormulaArray = insertOperatorIntoFormulaArray(newFormulaArray, {
        operator: button.id
      });
    }
    // const newValue = (
    //   <>
    //     {currentValue}
    //     <OpBlock $opId={button.id}>{button.label}</OpBlock>
    //     <EditablePosition />
    //   </>
    // );
    let newOpenBracketCount = currentlyOpenBrackets;
    onChangedFormula(newFormulaArray);
    if (button.id === 'openBrackets') {
      newOpenBracketCount += 1;
    } else if (button.id === 'closeBrackets') {
      newOpenBracketCount -= 1;
    }
    updateDisabledButtons(
      button.id,
      newOpenBracketCount,
      isCurrentlyInFunction
    );
    if (button.id === 'closeBrackets') {
      // setIsCurrentlyInFunction(false);
    }
    setCurrentlyOpenBrackets(newOpenBracketCount);
    setCurrentCaretPosition(currentCaretPosition + 1);
    setFormulaArray(newFormulaArray);
    setLastInserted(button.id);
    // setCurrentValue(newValue);
  };

  const getFunctionIndexToInsert = () => {
    let functionIndex = -1;
    let checkingCaretIndex = 0;
    formulaArrayWithIDs.every((item, index) => {
      if (item.values) {
        if (
          currentCaretPosition <=
          checkingCaretIndex + item.values.length + 1
        ) {
          functionIndex = currentCaretPosition - checkingCaretIndex - 1;
          return false;
        }
        checkingCaretIndex += item.values.length + 2;
      } else {
        checkingCaretIndex++;
      }
      if (checkingCaretIndex === currentCaretPosition) {
        return false;
      }
      return true;
    });
    return functionIndex;
  };

  const onClickField = field => {
    let newFormulaArray,
      newCaretPosition,
      functionValues,
      wasInFunctionWithValues;
    newFormulaArray = [...formulaArray];
    wasInFunctionWithValues = false;
    if (
      newFormulaArray.length &&
      isCurrentlyInFunction &&
      currentFunctionUUID
    ) {
      const indexOfMatchingFunction = formulaArrayWithIDs.findIndex(
        one => one.uuid === currentFunctionUUID
      );
      if (indexOfMatchingFunction !== -1) {
        let matchingFormulaElement = newFormulaArray[indexOfMatchingFunction];
        functionValues = matchingFormulaElement.values || [];
        wasInFunctionWithValues = functionValues.length;
        const whereToInsert = getFunctionIndexToInsert();
        functionValues.splice(whereToInsert, 0, { field: field.value });
        newCaretPosition = currentCaretPosition + 1;
      }
    } else {
      newFormulaArray = insertOperatorIntoFormulaArray(newFormulaArray, {
        field: field.value
      });
      newCaretPosition = currentCaretPosition + 1;
    }
    onChangedFormula(newFormulaArray);
    updateDisabledButtons(
      'field',
      currentlyOpenBrackets,
      isCurrentlyInFunction
    );
    if (wasInFunctionWithValues) {
      setTimeout(() => {
        setCurrentCaretPosition(newCaretPosition);
      }, 100);
    } else {
      setCurrentCaretPosition(newCaretPosition);
    }
    setFormulaArray(newFormulaArray);
    setLastInserted('field');
  };

  const onClickClear = () => {
    onChangedFormula([]);
    updateDisabledButtons('clear', 0, false);
    setLastInserted('');
    setCurrentlyOpenBrackets(0);
    setFormulaArray([]);
    setCurrentValue(<EditablePosition />);
    setCurrentCaretPosition(0);
    setIsCurrentlyInFunction(false);
    setCurrentFunctionUUID(null);
  };

  const onFocusValueField = e => {
    // only call setCaret if the valueField was clicked directly by the user, otherwise wait for the formula to change
    if (!e.nativeEvent.relatedTarget) {
      setTimeout(() => {
        setCaret();
      }, 1);
    }
  };

  const getChildNodeIndex = (el, previousBlock) => {
    if (!el || !previousBlock) {
      return -1; // Return -1 if the parent or child node is not valid
    }

    // Convert childNodes (NodeList) to an array and find the index
    let index = Array.prototype.indexOf.call(el.childNodes, previousBlock);

    return index;
  };

  useEffect(() => {
    setCaret();
  }, [formulaArrayWithIDs]);

  const setCaret = () => {
    let el = document.getElementById('field-calculator-value-field');
    let sel = window.getSelection();
    let targetBlock;
    if (sel.rangeCount > 0) {
      let range = sel.getRangeAt(0); // Get the current selection range
      let currentNode = sel.anchorNode; // The node where the current selection starts

      // Ensure we are working with an element node
      if (currentNode.nodeType === 3) {
        currentNode = currentNode.parentElement; // Get the parent element if it's a text node

        targetBlock = currentNode;
        while (
          targetBlock &&
          getBlockType(targetBlock) !== 'CursorPositionBlock'
        ) {
          targetBlock = targetBlock.nextElementSibling;
        }

        if (targetBlock) {
          let childNodeIndex = getChildNodeIndex(el, targetBlock);
          if (childNodeIndex === -1) {
            return;
          } else {
          }
        }
      } else {
        // find current location
        let currentLocationIndex = sel.anchorOffset;
        let i = currentLocationIndex,
          foundPreviousBreak = false;
        let gotToEnd = false;
        while (i >= 0 && i < 1000 && !foundPreviousBreak && !gotToEnd) {
          targetBlock = el.childNodes[i];
          if (!targetBlock) {
            gotToEnd = true;
            break;
          }
          if (getBlockType(targetBlock) === 'CursorPositionBlock') {
            foundPreviousBreak = true;
          } else {
            i = i + 1;
          }
        }

        if (gotToEnd) {
          return;
        } else if (!foundPreviousBreak) {
          targetBlock = el.childNodes[0];
        }

        if (i < 0) {
          i = 0;
        } else if (i > 10000) {
          i = currentLocationIndex;
        }
      }
      if (targetBlock && el?.childNodes) {
        let newCaretPosition = 0;
        let allCursorPositionBlocks = Array.from(el.childNodes).filter(
          one => getBlockType(one) === 'CursorPositionBlock'
        );
        if (allCursorPositionBlocks.length) {
          newCaretPosition = allCursorPositionBlocks.indexOf(targetBlock);
        }
        let previousBlockType = getPreviousBlockType(targetBlock);
        let newBracketCount = getBracketCountBefore(targetBlock);
        setCurrentCaretPosition(newCaretPosition);
        const isInFunction = getInFunctionStates(
          formulaArrayWithIDs,
          newCaretPosition
        );
        updateDisabledButtons(
          previousBlockType,
          newBracketCount,
          isInFunction[0]
        );
      }
    }
  };

  useEffect(() => {
    updateInFunctionStates(formulaArrayWithIDs, currentCaretPosition);
  }, [currentCaretPosition, formulaArrayWithIDs]);

  const moveCaret = dir => {
    let el = document.getElementById('field-calculator-value-field');
    let sel = window.getSelection();
    let targetBlock;

    if (sel.rangeCount > 0) {
      let range = sel.getRangeAt(0); // Get the current selection range
      let currentNode = sel.anchorNode; // The node where the current selection starts

      // Ensure we are working with an element node
      if (currentNode.nodeType === 3) {
        currentNode = currentNode.parentElement; // Get the parent element if it's a text node

        // Traverse closest previous or next `.CursorPositionBlock`
        targetBlock = currentNode;
        while (getBlockType(targetBlock) !== 'CursorPositionBlock') {
          if (dir === 1) {
            targetBlock = targetBlock.nextElementSibling;
          } else {
            targetBlock = targetBlock.previousElementSibling;
          }
        }

        if (targetBlock) {
          // Update the selection to the found `.CursorPositionBlock`

          let childNodeIndex = getChildNodeIndex(el, targetBlock);
          if (childNodeIndex === -1) {
            return;
          } else {
            /*range.setStart(el, getChildNodeIndex(el, targetBlock));
            range.collapse(true);

            sel.removeAllRanges();
            sel.addRange(range);*/
          }
        }
      } else {
        // find current location
        let currentLocationIndex = sel.anchorOffset;
        let i = currentLocationIndex + dir,
          foundPreviousBreak = false;
        let gotToEnd = false;
        while (i >= 0 && i < 1000 && !foundPreviousBreak && !gotToEnd) {
          targetBlock = el.childNodes[i];
          if (!targetBlock) {
            gotToEnd = true;
            break;
          }
          if (getBlockType(targetBlock) === 'CursorPositionBlock') {
            foundPreviousBreak = true;
          } else {
            i = i + dir;
          }
        }

        if (gotToEnd) {
          return;
        } else if (!foundPreviousBreak) {
          targetBlock = el.childNodes[0];
        }

        if (i < 0) {
          i = 0;
        } else if (i > 10000) {
          i = currentLocationIndex;
        }
        /*
        range.setStart(el, i);
        range.collapse(true);

        sel.removeAllRanges();
        sel.addRange(range);
        */
      }
      if (targetBlock && el?.childNodes) {
        let newCaretPosition = 0;
        let allCursorPositionBlocks = Array.from(el.childNodes).filter(
          one => getBlockType(one) === 'CursorPositionBlock'
        );
        if (allCursorPositionBlocks.length) {
          newCaretPosition = allCursorPositionBlocks.indexOf(targetBlock);
        }
        let previousBlockType = getPreviousBlockType(targetBlock);
        let newBracketCount = getBracketCountBefore(targetBlock);
        const isInFunction = updateInFunctionStates(
          formulaArrayWithIDs,
          newCaretPosition
        );
        updateDisabledButtons(previousBlockType, newBracketCount, isInFunction);
        setCurrentCaretPosition(newCaretPosition);
      }
    }
  };

  const onKeyUpValueField = e => {
    e.preventDefault();
  };

  const removePreviousBlock = () => {
    let newFormulaArray,
      removedBlockType,
      newFormulaArrayWithIDs = [];
    let sel = window.getSelection();
    let uuid, targetBlock;

    if (sel.rangeCount > 0) {
      let currentNode = sel.anchorNode; // The node where the current selection starts

      if (currentNode.nodeType === 3) {
        currentNode = currentNode.parentElement; // Get the parent element if it's a text node
      }

      let currentLocationIndex = sel.anchorOffset;

      // Traverse closest previous item that has a data-uuid
      targetBlock = currentNode.childNodes[currentLocationIndex];
      while (
        targetBlock &&
        (!targetBlock.dataset || !targetBlock.dataset.uuid)
      ) {
        targetBlock = targetBlock.previousElementSibling;
      }

      if (targetBlock) {
        uuid = targetBlock.dataset.uuid;
        removedBlockType = targetBlock.dataset.blocktype;
      }
    }
    if (uuid) {
      newFormulaArrayWithIDs = deepCopy(formulaArrayWithIDs).filter(
        item => item.uuid !== uuid
      );
      newFormulaArray = deepCopy(newFormulaArrayWithIDs).map(item => {
        let newItem = { ...item };
        if (newItem.uuid) {
          delete newItem.uuid;
        }
        if (newItem.values) {
          newItem.values = newItem.values.filter(one => one.uuid !== uuid);
        }
        return newItem;
      });
      let newPreviousBlockType = getPreviousBlockType(targetBlock);
      let newBracketCount = getBracketCountBefore(targetBlock);

      updateDisabledButtons(
        newPreviousBlockType,
        newBracketCount,
        getIsBlockInFunction(targetBlock.previousElementSibling)
      );
      onChangedFormula(newFormulaArray);
      setCurrentCaretPosition(currentCaretPosition - 1);
      setFormulaArrayWithIDs(newFormulaArrayWithIDs);
      setFormulaArray(newFormulaArray);
    }
  };

  const onKeyDownValueField = e => {
    e.preventDefault();
    if (e.key === 'ArrowLeft') {
      moveCaret(-1);
    } else if (e.key === 'ArrowRight') {
      moveCaret(1);
    } else if (e.key === 'Backspace') {
      removePreviousBlock();
    }
  };

  const onInputValueField = e => {};

  const viewProps = {
    currentValue,
    currentCaretPosition,
    disabledButtons,
    fields,
    isCurrentlyInFunction,
    onClickNumberButton,
    onClickFunctionButton,
    onClickButton,
    onClickField,
    onClickClear,
    onKeyUpValueField,
    onKeyDownValueField,
    onInputValueField,
    onFocusValueField,
    removePreviousBlock,
    validationError
  };

  return <FieldCalculatorView {...viewProps} />;
};

export default FieldCalculatorEngine;
